import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  Fab,
  Grid,
  IconButton,
  Stack,
  Skeleton,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  Divider,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FilterListIcon from '@mui/icons-material/FilterList';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import NavigationBar from '../components/NavigationBar';
import { getProductosPaged } from '../services/modules/Productos';
import { getCategorias } from '../services/modules/Categorias';
import { getMarcas } from '../services/modules/Marcas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BRANDING } from '../config/branding';

const fallbackImage = 'https://via.placeholder.com/320x220.png?text=Producto';
const currencyGTQ = new Intl.NumberFormat('es-GT', {
  style: 'currency',
  currency: 'GTQ',
  minimumFractionDigits: 2,
});
const PAGE_SIZE = 25;
const WHATSAPP_TABLE_COLS = {
  qty: 4,
  product: 38,
  price: 12,
  subtotal: 12,
};

const padLeft = (value, width) => String(value ?? '').padStart(width, ' ');
const padRight = (value, width) => String(value ?? '').padEnd(width, ' ');
const truncate = (value, max) => {
  const str = String(value ?? '');
  if (str.length <= max) return str;
  if (max <= 1) return str.slice(0, max);
  return `${str.slice(0, max - 1)}…`;
};

const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
};

const upgradeToHttpsIfNeeded = (value) => {
  const url = String(value || '');
  if (!url) return url;
  if (typeof window === 'undefined') return url;
  if (window.location?.protocol !== 'https:') return url;
  return url.startsWith('http://') ? `https://${url.slice('http://'.length)}` : url;
};

const getUrlBases = (rawInput) => {
  const raw = String(rawInput || '').trim();
  if (!raw) return { origin: '', base: '' };
  const noTrail = raw.replace(/\/$/, '');
  try {
    const url = new URL(noTrail);
    const basePath = String(url.pathname || '').replace(/\/$/, '');
    return { origin: url.origin, base: `${url.origin}${basePath}` };
  } catch {
    return { origin: noTrail, base: noTrail };
  }
};

const resolveCatalogImage = (value) => {
  if (!value) return fallbackImage;
  if (/^https?:\/\//i.test(value)) return upgradeToHttpsIfNeeded(value);
  const { origin: apiOrigin, base: apiBase } = getUrlBases(BRANDING.apiUrl);
  const { origin: uploadOrigin, base: uploadBase } = getUrlBases(BRANDING.uploadUrl || BRANDING.apiUrl);
  const normalized = value.startsWith('/') ? value : `/${value}`;
  const isUpload = normalized.startsWith('/uploads/') || normalized.startsWith('/upload/');
  const outBase = isUpload ? (uploadOrigin || uploadBase) : (apiBase || apiOrigin);
  const out = outBase ? `${outBase}${normalized}` : normalized;
  return upgradeToHttpsIfNeeded(out);
};

const CatalogoPage = () => {
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogPage, setCatalogPage] = useState(0);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [selectedCategoriaIds, setSelectedCategoriaIds] = useState([]);
  const [selectedMarcaIds, setSelectedMarcaIds] = useState([]);
  const [categoriaSearch, setCategoriaSearch] = useState('');
  const [marcaSearch, setMarcaSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const debouncedSearch = useDebouncedValue(search, 350);
  const loadSeqRef = useRef(0);
  const [columns, setColumns] = useState(() => {
    const stored = typeof window !== 'undefined' ? Number(window.localStorage.getItem('catalog-columns')) : 0;
    const initial = Number.isFinite(stored) && stored >= 2 && stored <= 5 ? stored : 2;
    return initial;
  });

  const showSidePanels = mdUp && columns <= 3;

  const getStock = useCallback((value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }, []);

  const cartQtyById = useMemo(() => {
    const map = new Map();
    cart.forEach((item) => map.set(item.id, Number(item.quantity || 0)));
    return map;
  }, [cart]);

  const selectedCategoriaNames = useMemo(() => {
    const ids = new Set(selectedCategoriaIds.map(String));
    return categorias
      .filter((c) => ids.has(String(c?.id_categoria)))
      .map((c) => c?.nombre)
      .filter(Boolean);
  }, [categorias, selectedCategoriaIds]);

  const selectedMarcaNames = useMemo(() => {
    const ids = new Set(selectedMarcaIds.map(String));
    return marcas
      .filter((m) => ids.has(String(m?.id_marca)))
      .map((m) => m?.nombre)
      .filter(Boolean);
  }, [marcas, selectedMarcaIds]);

  const formatSelectedLabels = useCallback((labels, { max = 2 } = {}) => {
    const clean = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (clean.length <= max) return clean.join(', ');
    return `${clean.slice(0, max).join(', ')} +${clean.length - max}`;
  }, []);

  const totalPages = useMemo(() => {
    const safeTotal = Number(catalogTotal) || 0;
    return Math.max(1, Math.ceil(safeTotal / PAGE_SIZE));
  }, [catalogTotal]);

  const loadProductsPage = useCallback(
    async ({ pageIndex }) => {
      const seq = ++loadSeqRef.current;
      setCatalogLoading(true);
      setCatalogError(null);

      try {
        const response = await getProductosPaged({
          q: debouncedSearch?.trim() || '',
          id_categoria: selectedCategoriaIds.length ? selectedCategoriaIds.join(',') : '',
          id_marca: selectedMarcaIds.length ? selectedMarcaIds.join(',') : '',
          page: pageIndex,
          pageSize: PAGE_SIZE,
        });

        if (seq !== loadSeqRef.current) return;

        const list = Array.isArray(response?.items) ? response.items : [];
        const total = Number(response?.total) || 0;

        setCatalogTotal(total);
        setCatalogPage(pageIndex);
        setCatalog(list);
      } catch (error) {
        console.error('No se pudo cargar el catalogo publico', error);
        setCatalogError('No pudimos cargar los productos en este momento.');
        setCatalog([]);
      } finally {
        if (seq === loadSeqRef.current) {
          setCatalogLoading(false);
        }
      }
    },
    [debouncedSearch, selectedCategoriaIds, selectedMarcaIds]
  );

  useEffect(() => {
    setCatalog([]);
    setCatalogTotal(0);
    setCatalogPage(0);
    loadProductsPage({ pageIndex: 0 });
  }, [debouncedSearch, selectedCategoriaIds, selectedMarcaIds, loadProductsPage]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [catsRes, marcasRes] = await Promise.allSettled([
          getCategorias(),
          getMarcas({ activos: '1' }),
        ]);
        const cats = catsRes.status === 'fulfilled' ? catsRes.value : [];
        const marcasList = marcasRes.status === 'fulfilled' ? marcasRes.value : [];
        setCategorias(Array.isArray(cats) ? cats : []);
        setMarcas(Array.isArray(marcasList) ? marcasList : []);
      } catch (error) {
        console.error('No se pudieron cargar filtros del catálogo', error);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('catalog-columns', String(columns));
  }, [columns]);

  const handleAddToCart = (product) => {
    const stock = getStock(product?.stock);
    if (stock <= 0) return;

    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        if (Number(existing.quantity || 0) >= stock) return prev;
        return prev.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    if (!mdUp) setCartDrawerOpen(true);
  };

  const handleUpdateQuantity = (id, delta) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id !== id) return item;
          const stock = getStock(item?.stock);
          const current = Number(item.quantity || 0);
          const next = Math.max(1, current + delta);
          if (stock <= 0) return { ...item, quantity: next };
          return { ...item, quantity: Math.min(next, stock) };
        })
        .filter((item) => item.quantity > 0)
    );
  };

  const handleRemoveFromCart = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.precio_venta || 0) * item.quantity, 0),
    [cart]
  );

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [cart]
  );

  const handleChangePage = useCallback(
    (_event, pageUi) => {
      const nextPage = Math.min(Math.max((Number(pageUi) || 1) - 1, 0), totalPages - 1);
      if (nextPage === catalogPage) return;
      loadProductsPage({ pageIndex: nextPage });
    },
    [catalogPage, loadProductsPage, totalPages]
  );

  const handleClearFilters = useCallback(() => {
    setSelectedCategoriaIds([]);
    setSelectedMarcaIds([]);
    setCategoriaSearch('');
    setMarcaSearch('');
  }, []);

  const filteredCategorias = useMemo(() => {
    const q = String(categoriaSearch || '').trim().toLowerCase();
    const list = Array.isArray(categorias) ? categorias : [];
    if (!q) return list;
    return list.filter((c) => String(c?.nombre || '').toLowerCase().includes(q));
  }, [categorias, categoriaSearch]);

  const filteredMarcas = useMemo(() => {
    const q = String(marcaSearch || '').trim().toLowerCase();
    const list = Array.isArray(marcas) ? marcas : [];
    if (!q) return list;
    return list.filter((m) => String(m?.nombre || '').toLowerCase().includes(q));
  }, [marcas, marcaSearch]);

  const toggleSelectedId = useCallback((current, id) => {
    const target = String(id);
    const exists = current.some((x) => String(x) === target);
    if (exists) return current.filter((x) => String(x) !== target);
    return [...current, target];
  }, []);

  const FiltersContent = (
    <Stack spacing={2}>
      <Typography variant="h6" fontWeight={800}>
        Organizar
      </Typography>

      <Divider />

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
          <Typography variant="subtitle2" fontWeight={800}>
            Marcas
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedCategoriaIds.length ? `${selectedCategoriaIds.length} seleccionada(s)` : 'Todas'}
          </Typography>
        </Stack>
        <TextField
          label="Buscar marca"
          size="small"
          value={categoriaSearch}
          onChange={(e) => setCategoriaSearch(e.target.value)}
        />
        <Box
          sx={{
            maxHeight: 220,
            overflow: 'auto',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <FormGroup>
            {filteredCategorias.map((cat) => {
              const id = String(cat?.id_categoria ?? '');
              const checked = selectedCategoriaIds.some((x) => String(x) === id);
              return (
                <FormControlLabel
                  key={id || cat?.nombre}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => setSelectedCategoriaIds((prev) => toggleSelectedId(prev, id))}
                      size="small"
                    />
                  }
                  label={cat?.nombre || 'Sin nombre'}
                />
              );
            })}
            {filteredCategorias.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
                Sin categorías
              </Typography>
            )}
          </FormGroup>
        </Box>
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
          <Typography variant="subtitle2" fontWeight={800}>
            Categorías
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedMarcaIds.length ? `${selectedMarcaIds.length} seleccionada(s)` : 'Todas'}
          </Typography>
        </Stack>
        <TextField
          label="Buscar categoría"
          size="small"
          value={marcaSearch}
          onChange={(e) => setMarcaSearch(e.target.value)}
        />
        <Box
          sx={{
            maxHeight: 220,
            overflow: 'auto',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <FormGroup>
            {filteredMarcas.map((marca) => {
              const id = String(marca?.id_marca ?? '');
              const checked = selectedMarcaIds.some((x) => String(x) === id);
              return (
                <FormControlLabel
                  key={id || marca?.nombre}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => setSelectedMarcaIds((prev) => toggleSelectedId(prev, id))}
                      size="small"
                    />
                  }
                  label={marca?.nombre || 'Sin nombre'}
                />
              );
            })}
            {filteredMarcas.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
                Sin marcas
              </Typography>
            )}
          </FormGroup>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button variant="outlined" onClick={handleClearFilters} sx={{ borderRadius: 999 }}>
          Limpiar
        </Button>
        {!showSidePanels && (
          <Button variant="contained" onClick={() => setFiltersDrawerOpen(false)} sx={{ borderRadius: 999 }}>
            Ver productos
          </Button>
        )}
      </Stack>
    </Stack>
  );

  const handleSendWhatsApp = () => {
    if (!cart.length) return;

    const { qty, product, price, subtotal } = WHATSAPP_TABLE_COLS;

    const header =
      `${padRight('Cant', qty)}  ` +
      `${padRight('Producto', product)}  ` +
      `${padLeft('Precio', price)}  ` +
      `${padLeft('Subtotal', subtotal)}`;

    const separator =
      `${'-'.repeat(qty)}  ` +
      `${'-'.repeat(product)}  ` +
      `${'-'.repeat(price)}  ` +
      `${'-'.repeat(subtotal)}`;

    const rows = cart.map((item) => {
      const qtyValue = Number(item.quantity || 0);
      const priceValue = Number(item.precio_venta || 0);
      const subtotalValue = qtyValue * priceValue;
      const productLabel = `${item.codigo || 'SIN CÓDIGO'} - ${item.descripcion || ''}`;

      return (
        `${padRight(qtyValue, qty)}  ` +
        `${padRight(truncate(productLabel, product), product)}  ` +
        `${padLeft(currencyGTQ.format(priceValue), price)}  ` +
        `${padLeft(currencyGTQ.format(subtotalValue), subtotal)}`
      );
    });

    const tableBlock = ['```', header, separator, ...rows, '```'].join('\n');

    const message = [
      'Hola, me interesa este pedido:',
      tableBlock,
      '',
      `*Total General:* *${currencyGTQ.format(cartTotal)}*`,
    ].join('\n');

    const url = `https://wa.me/${BRANDING.whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const buildQuotePdf = useCallback(() => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const now = new Date();
    const dateText = now.toLocaleString('es-GT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(BRANDING.pdf.name, 40, 48);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Cotización`, 40, 70);
    doc.text(`Fecha: ${dateText}`, 40, 86);

    const rows = cart.map((item, index) => {
      const qty = Number(item.quantity || 0);
      const price = Number(item.precio_venta || 0);
      const subtotal = qty * price;
      return [
        String(index + 1),
        String(item.codigo || ''),
        String(item.descripcion || ''),
        String(qty),
        currencyGTQ.format(price),
        currencyGTQ.format(subtotal),
      ];
    });

    autoTable(doc, {
      startY: 110,
      head: [['#', 'Código', 'Descripción', 'Cant', 'Precio', 'Subtotal']],
      body: rows,
      styles: { fontSize: 10, cellPadding: 6 },
      headStyles: { fillColor: [30, 64, 175] },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 72 },
        2: { cellWidth: 240 },
        3: { cellWidth: 44, halign: 'right' },
        4: { cellWidth: 72, halign: 'right' },
        5: { cellWidth: 78, halign: 'right' },
      },
    });

    const lastY = doc.lastAutoTable?.finalY || 110;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`Total aproximado: ${currencyGTQ.format(cartTotal)}`, 40, lastY + 28);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(
      'Esta cotización es referencial y puede variar según existencias y confirmación final.',
      40,
      lastY + 48
    );

    return doc;
  }, [cart, cartTotal]);

  const handleShareQuotePdf = useCallback(async () => {
    if (!cart.length) return;

    const doc = buildQuotePdf();
    const pdfBlob = doc.output('blob');
    const filename = `cotizacion-${new Date().toISOString().slice(0, 10)}.pdf`;

    try {
      const file = new File([pdfBlob], filename, { type: 'application/pdf' });
      const canShareFiles =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });

      if (canShareFiles) {
        await navigator.share({
          title: 'Cotización',
          text: `${BRANDING.companyName} - Cotización`,
          files: [file],
        });
        return;
      }
    } catch (error) {
      // fallback below
      console.warn('No se pudo compartir el PDF, usando descarga como fallback', error);
    }

    // Fallback: descarga local (el usuario puede adjuntarlo manualmente en WhatsApp)
    doc.save(filename);
  }, [buildQuotePdf, cart.length]);

  const CartContent = (
    <Stack spacing={2}>
      {!cart.length ? (
        <Typography color="text.secondary">
          Aun no has agregado productos. Selecciona un articulo del catalogo.
        </Typography>
      ) : (
        <>
          {cart.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600}>{item.descripcion}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.codigo || 'SIN CODIGO'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stock: {getStock(item?.stock)}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton color="primary" onClick={() => handleUpdateQuantity(item.id, -1)}>
                  <RemoveIcon />
                </IconButton>
                <Typography fontWeight={600}>{item.quantity}</Typography>
                <IconButton
                  color="primary"
                  onClick={() => handleUpdateQuantity(item.id, 1)}
                  disabled={(() => {
                    const stock = getStock(item?.stock);
                    if (stock <= 0) return true;
                    return Number(item.quantity || 0) >= stock;
                  })()}
                >
                  <AddIcon />
                </IconButton>
              </Stack>
              <Typography width={110} textAlign="right" fontWeight={600}>
                {currencyGTQ.format(Number(item.precio_venta || 0) * item.quantity)}
              </Typography>
              <IconButton color="error" onClick={() => handleRemoveFromCart(item.id)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" pt={1}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Total estimado: {currencyGTQ.format(cartTotal)}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleShareQuotePdf}
              disabled={!cart.length}
              sx={{ borderRadius: 999 }}
              fullWidth={!mdUp}
            >
              Cotización PDF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              onClick={handleSendWhatsApp}
              disabled={!cart.length}
              sx={{ borderRadius: 999 }}
              fullWidth={!mdUp}
            >
              Enviar via WhatsApp
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );

  return (
    <>
      <NavigationBar />
      <Box
        component="main"
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          py: 6,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1} mb={4}>
            <Chip label="Catalogo en linea" sx={{ width: 'fit-content' }} />
            <Typography variant="h4" fontWeight={700}>
              Pide tus productos favoritos
            </Typography>
            <Typography variant="body1" color="text.secondary" maxWidth={640}>
              Selecciona los articulos que necesitas y envianos tu carrito directo a WhatsApp.
            </Typography>
            <Box
              component="form"
              onSubmit={(e) => e.preventDefault()}
              sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}
            >
              {!showSidePanels && (
                <IconButton
                  onClick={() => setFiltersDrawerOpen(true)}
                  aria-label="Abrir filtros"
                  sx={{
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <FilterListIcon />
                </IconButton>
              )}
              <TextField
                placeholder="Buscar por nombre o código"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 240 }}
              />
              {!showSidePanels && (
                <IconButton
                  onClick={() => setCartDrawerOpen(true)}
                  aria-label="Abrir carrito"
                  sx={{
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Badge color="primary" badgeContent={cartCount} max={99} invisible={!cartCount}>
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              )}
              {search && (
                <Button variant="outlined" onClick={() => setSearch('')}>
                  Limpiar
                </Button>
              )}
            </Box>

            {lgUp && (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Typography variant="body2" color="text.secondary">
                  Productos por fila:
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={columns}
                  onChange={(_e, value) => {
                    if (!value) return;
                    const next = Math.min(Math.max(Number(value) || 2, 2), 5);
                    setColumns(next);
                  }}
                  size="small"
                >
                  {[2, 3, 4, 5].map((n) => (
                    <ToggleButton key={n} value={n}>
                      {n}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Mostrando {catalog.length} de {catalogLoading && catalog.length === 0 ? '...' : catalogTotal} productos
              </Typography>
              {!!debouncedSearch?.trim() && (
                <Chip
                  size="small"
                  label={`Búsqueda: ${debouncedSearch.trim()}`}
                  sx={{ width: 'fit-content' }}
                />
              )}
              {!!selectedCategoriaIds.length && (
                <Chip
                  size="small"
                  label={`Marcas: ${formatSelectedLabels(selectedCategoriaNames)}`}
                />
              )}
              {!!selectedMarcaIds.length && (
                <Chip size="small" label={`Categorías: ${formatSelectedLabels(selectedMarcaNames)}`} />
              )}
            </Stack>
          </Stack>

          {catalogLoading && catalog.length === 0 && (
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <CircularProgress size={24} />
              <Typography>Cargando catalogo...</Typography>
            </Stack>
          )}

          {catalogError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {catalogError}
            </Alert>
          )}

          <Grid container spacing={3}>
            {showSidePanels && (
              <Grid item md={3}>
                <Card sx={{ position: 'sticky', top: 32, borderRadius: 3, p: 2 }}>
                  {FiltersContent}
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={showSidePanels ? 6 : 12}>
              {!showSidePanels && (
                <Drawer
                  anchor="left"
                  open={filtersDrawerOpen}
                  onClose={() => setFiltersDrawerOpen(false)}
                  PaperProps={{ sx: { width: 320, p: 2 } }}
                >
                  {FiltersContent}
                </Drawer>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: `repeat(${Math.min(columns, 4)}, minmax(0, 1fr))`,
                    lg: `repeat(${Math.min(columns, 5)}, minmax(0, 1fr))`,
                    xl: `repeat(${columns}, minmax(0, 1fr))`,
                  },
                }}
              >
                {catalogLoading && catalog.length === 0
                  ? Array.from({ length: 10 }).map((_, index) => (
                      <Card key={`skeleton-${index}`} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: '100%', aspectRatio: '1 / 1' }}>
                          <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
                        </Box>
                        <CardContent>
                          <Stack spacing={1}>
                            <Skeleton width="45%" />
                            <Skeleton width="90%" />
                            <Skeleton width="65%" />
                            <Skeleton width="40%" height={36} />
                          </Stack>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Skeleton variant="circular" width={40} height={40} sx={{ ml: 'auto' }} />
                        </CardActions>
                      </Card>
                    ))
                  : catalog.map((product) => {
                      const stock = getStock(product?.stock);
                      const inCart = Number(cartQtyById.get(product.id) || 0);
                      const canAdd = stock > 0 && inCart < stock;
                      const stockLabel = stock > 0 ? `Stock: ${stock}` : 'Agotado';
                      const addTooltip = canAdd
                        ? 'Agregar al carrito'
                        : stock <= 0
                          ? 'Agotado'
                          : 'Máximo disponible en carrito';

                      return (
                      <Card
                        key={product.id}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          overflow: 'hidden',
                          transition: 'transform 180ms ease, box-shadow 180ms ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 10,
                          },
                        }}
                      >
                        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1' }}>
                          <Box
                            component="img"
                            src={resolveCatalogImage(product.imagen_url)}
                            alt={product.descripcion}
                            loading="lazy"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              background:
                                'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%)',
                            }}
                          />
                          {columns <= 3 && (
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ position: 'absolute', left: 12, top: 12, right: 12 }}
                            >
                              <Chip
                                size="small"
                                label={product.marca_categoria || product.categoria || 'General'}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.86)',
                                  fontWeight: 600,
                                }}
                              />
                              {stock > 0 && stock <= Number(product.nivel_minimo || 0) && (
                                <Chip size="small" color="warning" label="Stock bajo" />
                              )}
                            </Stack>
                          )}
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          <Stack spacing={0.75}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {product.codigo || 'SIN CÓDIGO'}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{
                                lineHeight: 1.15,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {product.descripcion}
                            </Typography>
                            {columns <= 3 && product.categoria && (
                              <Typography variant="body2" color="text.secondary">
                                {product.categoria}
                              </Typography>
                            )}
                            <Typography variant="caption" color={stock > 0 ? 'text.secondary' : 'error.main'}>
                              {stockLabel}
                            </Typography>
                            <Typography variant="h6" fontWeight={800} color="primary.main">
                              {currencyGTQ.format(Number(product.precio_venta) || 0)}
                            </Typography>
                          </Stack>
                        </CardContent>
                        <CardActions sx={{ p: 1.5, pt: 0, justifyContent: 'flex-end' }}>
                          <Tooltip title={addTooltip}>
                            <span>
                              <IconButton
                                onClick={() => handleAddToCart(product)}
                                aria-label="Agregar al carrito"
                                disabled={!canAdd}
                                sx={{
                                  color: 'common.white',
                                  backgroundImage: 'linear-gradient(90deg, #00d4ff, #7c3aed)',
                                  '&:hover': { opacity: 0.92 },
                                  '&.Mui-disabled': {
                                    color: 'rgba(255,255,255,0.7)',
                                    backgroundImage: 'linear-gradient(90deg, rgba(100,116,139,0.7), rgba(148,163,184,0.6))',
                                  },
                                }}
                              >
                                <ShoppingCartIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </CardActions>
                      </Card>
                      );
                    })}
              </Box>

              {totalPages > 1 && (
                <Stack spacing={1.5} alignItems="center" mt={3}>
                  {catalogLoading && catalog.length > 0 && (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Cargando página...
                      </Typography>
                    </Stack>
                  )}
                  <Pagination
                    page={catalogPage + 1}
                    count={totalPages}
                    onChange={handleChangePage}
                    color="primary"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Stack>
              )}
            </Grid>
            {showSidePanels && (
              <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Card sx={{ position: 'sticky', top: 32, borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <ShoppingCartIcon />
                    <Typography variant="h6" fontWeight={700}>
                      Tu carrito ({cart.length})
                    </Typography>
                  </Stack>
                  {CartContent}
                </CardContent>
              </Card>
              </Grid>
            )}
          </Grid>
        </Container>

        {!mdUp && cart.length > 0 && (
          <Fab
            color="primary"
            onClick={() => setCartDrawerOpen(true)}
            sx={{
              position: 'fixed',
              left: 24,
              bottom: 24,
              boxShadow: 6,
            }}
          >
            <ShoppingCartIcon />
          </Fab>
        )}

        <Drawer
          anchor="bottom"
          open={cartDrawerOpen}
          onClose={() => setCartDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              p: 3,
            },
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ShoppingCartIcon />
              <Typography variant="h6" fontWeight={700}>
                Tu carrito ({cart.length})
              </Typography>
            </Stack>
            {CartContent}
          </Stack>
        </Drawer>
      </Box>
    </>
  );
};

export default CatalogoPage;
